import { useCallback, useMemo } from 'react'
import { PlanElementType, SelectedPlanElement, useSelectionStore } from '@editorStores'

/**
 * Plan element (comment / table / image card / element label) selection backed by the global
 * selection store's `planSelection` slot — drop-in replacement for the former
 * `controlStore.selectedPlanElement`. These selections are mutually exclusive singles, but they
 * are independent of the element selection: selecting a comment neither changes the mode nor
 * drops the selected element (its form stays in the drawer while the comment is edited in the
 * scene), and clearing only clears the plan selection.
 */
const usePlanElementSelection = () => {
  // Scoped primitive selectors so unrelated selection changes don't re-render consumers (several
  // of them live in the draw-call-sensitive scene tree).
  const planElementType = useSelectionStore(state => state.planSelection?.planElementType ?? null)
  const planElementGuid = useSelectionStore(state => state.planSelection?.guid ?? null)
  const selectPlanElementInStore = useSelectionStore(state => state.selectPlanElement)

  const selectedPlanElement: SelectedPlanElement | null = useMemo(
    () =>
      planElementType && planElementGuid ? { type: planElementType, id: planElementGuid } : null,
    [planElementType, planElementGuid],
  )

  const selectPlanElement = useCallback(
    (type: PlanElementType, id: string) =>
      selectPlanElementInStore({ type: 'plan', planElementType: type, guid: id }),
    [selectPlanElementInStore],
  )

  const clearPlanSelection = useCallback(
    () => selectPlanElementInStore(null),
    [selectPlanElementInStore],
  )

  return { selectedPlanElement, selectPlanElement, clearPlanSelection }
}

export default usePlanElementSelection
